import { useCallback, useEffect, useState } from "react";
import { Star, CheckCircle, XCircle, ArrowsClockwise, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * ProAccessRequestsCard — admin queue for free owners who clicked
 * "Request Pro access" on /upgrade while Stripe was disabled (or while
 * we run Pro as private beta).
 *
 * Approve flips `users.is_pro=true` on the requester. Reject leaves
 * the user unchanged.
 *
 * Backend:
 *   GET  /api/admin/pro-access-requests              → pending list
 *   POST /api/admin/pro-access-requests/{id}/approve → flip is_pro=true
 *   POST /api/admin/pro-access-requests/{id}/reject  → mark rejected
 */
export default function ProAccessRequestsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pro-access-requests");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't load requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/admin/pro-access-requests/${id}/${action}`, {});
      toast.success(action === "approve" ? "Pro granted." : "Request rejected.");
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || `Couldn't ${action}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5 sm:p-6"
      data-testid="pro-access-requests-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Star weight="duotone" className="h-5 w-5 text-primary" />
            <h2 className="font-display font-bold text-lg sm:text-xl">Pro access requests</h2>
            <span
              className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded-full bg-primary/15 text-primary text-[11px] font-bold"
              data-testid="pro-access-requests-count"
            >
              {rows.length}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Owners who clicked "Request Pro access" on <span className="font-mono">/upgrade</span>.
            Approve to grant Pro instantly.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50"
          data-testid="pro-access-requests-refresh"
          aria-label="Refresh"
        >
          <ArrowsClockwise className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground" data-testid="pro-access-requests-loading">
          <Spinner className="h-5 w-5 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground" data-testid="pro-access-requests-empty">
          No pending requests. (When an owner clicks "Request Pro access" on <span className="font-mono">/upgrade</span>, they'll appear here.)
        </div>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((r) => (
            <li
              key={r.id}
              className="rounded-xl border border-border bg-background p-3.5"
              data-testid={`pro-access-request-${r.id}`}
            >
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                    User
                  </div>
                  <div className="text-sm font-semibold truncate" data-testid="pro-access-request-email">
                    {r.email}
                  </div>
                  {r.name && (
                    <div className="text-[11px] text-muted-foreground truncate">{r.name}</div>
                  )}
                </div>
                <div className="flex-1 min-w-[180px]">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                    Submitted
                  </div>
                  <div className="text-sm">
                    {new Date(r.created_at).toLocaleString([], {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
              {r.note && (
                <div className="mt-2.5 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground italic">
                  "{r.note}"
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => decide(r.id, "reject")}
                  disabled={busyId === r.id}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50"
                  data-testid="pro-access-request-reject-btn"
                >
                  <XCircle weight="duotone" className="h-3.5 w-3.5" /> Reject
                </button>
                <button
                  type="button"
                  onClick={() => decide(r.id, "approve")}
                  disabled={busyId === r.id}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="pro-access-request-approve-btn"
                >
                  <CheckCircle weight="fill" className="h-3.5 w-3.5" />
                  {busyId === r.id ? "Granting…" : "Approve & grant Pro"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
